import { useEffect, useState } from "react";
import { JobTypeLabel, ReqPriorityLabel, ReqTermLabel, ReqTypeLabel, TRequirement } from "../requirement-my-sales/modal";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { ProgressSpinner } from "primereact/progressspinner";
import { Fetch } from "../../utilities/fetch";

const InfoSection = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => (
    <div
        style={{
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            overflow: 'hidden',
            background: '#fff',
        }}
    >
        <div
            style={{
                padding: '8px 12px',
                background: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                fontSize: '12px',
                fontWeight: 600,
                color: '#334155',
            }}
        >
            {title}
        </div>

        <div style={{ padding: '10px 12px' }}>
            {children}
        </div>
    </div>
);

const InfoCard = ({
    title,
    items,
}: {
    title: string;
    items: [string, React.ReactNode][];
}) => (
    <InfoSection title={title}>
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '7px',
            }}
        >
            {items.map(([label, value]) => (
                <InfoItem
                    key={label}
                    label={label}
                    value={value}
                />
            ))}
        </div>
    </InfoSection>
);

const InfoItem = ({
    label,
    value,
}: {
    label: string;
    value?: React.ReactNode;
}) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: '8px',
            minWidth: 0,
        }}
    >
        <span
            style={{
                width: '90px',
                flexShrink: 0,
                color: '#94a3b8',
                fontSize: '11px',
            }}
        >
            {label}
        </span>

        <span
            style={{
                color: '#334155',
                fontSize: '12px',
                fontWeight: 500,
                minWidth: 0,
                wordBreak: 'break-word',
            }}
        >
            {value ?? '-'}
        </span>
    </div>
);

const formatDate = (date?: string) => {
    if (!date) return '-';

    return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

interface Iprops {
    requirementId: number | null;
    hideDialog: () => void;
}

export const RequirementInfo = (props: Iprops) => {
    const [reqInfo, setReqInfo] = useState<TRequirement | null>(null);
    const [loading, setLoading] = useState(false);

    const loadInfo = async (reqId: number) => {
        setLoading(true);

        const result = await Fetch<TRequirement>(
            `SalesMyRequirement/${reqId}`
        );

        if (result) {
            setReqInfo(result);
        }

        setLoading(false);
    };

    useEffect(() => {
        if (props.requirementId) {
            loadInfo(props.requirementId);
        } else {
            setReqInfo(null);
        }
    }, [props.requirementId]);

    return (
        <Dialog
            visible={!!props.requirementId}
            style={{ width: '80vw', maxWidth: '1100px' }}
            header="Requirement Details"
            modal
            className="p-fluid"
            onHide={props.hideDialog}
        >
            {loading ? (
                <div
                    style={{
                        height: '300px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <ProgressSpinner style={{ width: '40px', height: '40px' }} />
                </div>
            ) : reqInfo ? (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                    }}
                >
                    {/* Header */}
                    <div
                        style={{
                            padding: '14px 16px',
                            borderRadius: '8px',
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '12px',
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    fontSize: '18px',
                                    fontWeight: 600,
                                    color: '#1e293b',
                                }}
                            >
                                {reqInfo.jobTitle}
                            </div>

                            <div
                                style={{
                                    marginTop: '4px',
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                    fontSize: '12px',
                                    color: '#64748b',
                                }}
                            >
                                <span>
                                    Req #{reqInfo.id}
                                </span>

                                <span>•</span>

                                <span>
                                    Posted by {reqInfo.postedBy}
                                </span>

                                <span>•</span>

                                <span>
                                    {formatDate(reqInfo.postedOn)}
                                </span>
                            </div>
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                gap: '6px',
                                flexWrap: 'wrap',
                            }}
                        >
                            {reqInfo.isHold && (
                                <Tag
                                    value="On Hold"
                                    severity="warning"
                                />
                            )}

                            {!reqInfo.isHold && (
                                <Tag
                                    value="Active"
                                    severity="success"
                                />
                            )}
                        </div>
                    </div>

                    {/* Main information */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns:
                                'repeat(3, minmax(0, 1fr))',
                            gap: '10px',
                        }}
                    >
                        <InfoCard
                            title="Client"
                            items={[
                                ['Vendor', reqInfo.vendorCompanyName],
                                ['Contact', reqInfo.contactPersonName],
                                ['End Client', reqInfo.endClient],
                            ]}
                        />

                        <InfoCard
                            title="Requirement"
                            items={[
                                ['Type', ReqTypeLabel[reqInfo.reqType]],
                                ['Job Type', JobTypeLabel[reqInfo.jobType]],
                                ['Term', ReqTermLabel[reqInfo.term]],
                                ['Positions', reqInfo.noOfPositions],
                            ]}
                        />

                        <InfoCard
                            title="Commercial"
                            items={[
                                ['Rate', reqInfo.rate],
                                ['Duration', `${reqInfo.duration}`],
                                ['Priority', ReqPriorityLabel[reqInfo.priority]],
                                ['Industry', reqInfo.industryName],
                            ]}
                        />
                    </div>

                    {/* Location */}
                    <InfoSection title="Location">
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(3, minmax(0, 1fr))',
                                gap: '10px',
                            }}
                        >
                            <InfoItem
                                label="Country"
                                value={reqInfo.countryName}
                            />

                            <InfoItem
                                label="State"
                                value={reqInfo.stateName}
                            />

                            <InfoItem
                                label="City"
                                value={reqInfo.cityName}
                            />
                        </div>
                    </InfoSection>

                    {/* Skills */}
                    <InfoSection title="Skills">
                        <InfoItem
                            label="Skill Category"
                            value={reqInfo.skillCategory}
                        />
                        <InfoItem
                            label="Required Skills"
                            value={reqInfo.requiredSkill || '-'}
                        />

                    </InfoSection>

                    {/* Interview / Additional */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns:
                                'repeat(2, minmax(0, 1fr))',
                            gap: '10px',
                        }}
                    >
                        <InfoSection title="Interview Process">
                            <InfoItem
                                label="Process"
                                value={reqInfo.interviewProcess}
                            />
                        </InfoSection>

                        <InfoSection title="Additional">
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '8px',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Tag
                                    value={
                                        reqInfo.isTravelRequired
                                            ? 'Travel Required'
                                            : 'No Travel'
                                    }
                                // severity={
                                //     reqInfo.isTravelRequired
                                //         ? 'warning'
                                //         : 'secondary'
                                // }
                                />
                            </div>
                        </InfoSection>
                    </div>

                    {/* Job Description */}
                    <InfoSection title="Job Description">
                        <div
                            style={{
                                color: '#475569',
                                fontSize: '13px',
                                lineHeight: 1.6,
                                maxHeight: '250px',
                                overflowY: 'auto',
                            }}
                            dangerouslySetInnerHTML={{
                                __html: reqInfo.jobDescription || '-',
                            }}
                        />
                    </InfoSection>
                </div>
            ) : null}
        </Dialog>
    );
};